/* useOperationalSignals.js — fetch the canonical seller-confidence +
 * solicitor-responsiveness payload for a single deal.
 *
 * Single GET → /api/operational-signals/deal/{deal_id}
 *
 * Shape:
 *   {
 *     deal_id,
 *     seller_confidence: { state, reasons, signals },
 *     solicitors: [{role, role_label, contact_id, name, signal: {state, reasons}}, ...]
 *   }
 *
 * The hook caches per-dealId in-memory so multiple components inside
 * the Deal Room (right rail, participants card, header) only fetch
 * once per deal load. Use `invalidateOperationalSignals(dealId)` to
 * force a refetch after a write.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const _cache = new Map(); // dealId → payload

export function useOperationalSignals(dealId, { refreshKey = 0 } = {}) {
  const [data, setData] = useState(() =>
    dealId ? _cache.get(dealId) || null : null,
  );
  const [loading, setLoading] = useState(!_cache.has(dealId));
  const inFlight = useRef(null);

  useEffect(() => {
    if (!dealId) {
      setData(null);
      setLoading(false);
      return;
    }
    if (refreshKey === 0 && _cache.has(dealId)) {
      setData(_cache.get(dealId));
      setLoading(false);
      return;
    }
    if (inFlight.current === dealId) return;
    inFlight.current = dealId;
    setLoading(true);

    (async () => {
      try {
        const { data: payload } = await api.get(
          `/api/operational-signals/deal/${dealId}`,
        );
        _cache.set(dealId, payload || null);
        if (inFlight.current === dealId) setData(payload || null);
      } catch {
        _cache.set(dealId, null);
        if (inFlight.current === dealId) setData(null);
      } finally {
        if (inFlight.current === dealId) setLoading(false);
        inFlight.current = null;
      }
    })();
  }, [dealId, refreshKey]);

  return { data, loading };
}

export function invalidateOperationalSignals(dealId) {
  if (dealId) _cache.delete(dealId);
  else _cache.clear();
}
